import React, { useMemo } from 'react';
import {
  RELACION_VENTAS_TITULO_DEFAULT,
  applyRelacionVentasColumnLabelsFromApi,
  buildRelacionFootCells,
  buildRelacionSegments,
  extractRelacionApiFootTotals,
  formatRelacionCell,
  mergeRelacionTotalsSlice,
  mergeRelacionVentasEncabezado,
  resolveRelacionVentasColumns,
  rowToRelacionNorm,
  sortRelacionRowsForDisplay,
  sumRelacionTotals,
  withRelacionVentasPeriodoLine,
} from '../../../utils/relacionVentasReportModel';
import { encabezadoTieneDatos } from '../../../utils/reporteEncabezadoApi';
import ReporteFiltrosEncabezadoLista from '../ReporteFiltrosEncabezadoLista/ReporteFiltrosEncabezadoLista';
import '../PopularidadReportLayout/PopularidadReportLayout.css';
import '../DescuentosCortesiasReportLayout/DescuentosCortesiasReportLayout.css';
import './RelacionVentasReportLayout.css';

const meaningfulHeaderLine = (v) => v != null && String(v).trim() !== '';

export default function RelacionVentasReportLayout({
  className = '',
  rawRows = [],
  fechaInicio,
  fechaFin,
  cajaInicio,
  cajaFin,
  turnoInicio,
  turnoFin,
  encabezadoApi = null,
  encabezadoFallback = null,
  reporteTitulo = RELACION_VENTAS_TITULO_DEFAULT,
  apiTotales = null,
  columnasLabels = null,
  ordenarMesa = false,
  agruparTurno = false,
  sucursalNombre = '',
}) {
  const enc = useMemo(() => {
    const m = mergeRelacionVentasEncabezado(encabezadoApi, encabezadoFallback);
    return withRelacionVentasPeriodoLine(
      { ...m, reporteTitulo: m.reporteTitulo || reporteTitulo },
      fechaInicio,
      fechaFin
    );
  }, [encabezadoApi, encabezadoFallback, reporteTitulo, fechaInicio, fechaFin]);

  const resolved = useMemo(() => {
    const base = resolveRelacionVentasColumns(rawRows[0] ?? null);
    return applyRelacionVentasColumnLabelsFromApi(base, columnasLabels);
  }, [rawRows, columnasLabels]);

  const sorted = useMemo(
    () => sortRelacionRowsForDisplay(rawRows, resolved, ordenarMesa),
    [rawRows, resolved, ordenarMesa]
  );

  const segments = useMemo(
    () => buildRelacionSegments(sorted, agruparTurno),
    [sorted, agruparTurno]
  );

  const { totalsVenta, totalsCortesia } = useMemo(() => {
    const normRows = sorted.map((r) => rowToRelacionNorm(r, resolved));
    const summed = sumRelacionTotals(normRows, resolved);
    const { venta: apiVenta, cortesia: apiCortesia } = extractRelacionApiFootTotals(apiTotales);
    const zeroTotals = Object.fromEntries(resolved.filter((c) => c.total).map((c) => [c.id, 0]));
    return {
      totalsVenta: mergeRelacionTotalsSlice(summed, apiVenta, resolved),
      totalsCortesia: mergeRelacionTotalsSlice(zeroTotals, apiCortesia, resolved),
    };
  }, [sorted, resolved, apiTotales]);

  const footVenta = useMemo(
    () => buildRelacionFootCells(resolved, 'TOTAL VENTA', totalsVenta),
    [resolved, totalsVenta]
  );
  const footCortesia = useMemo(
    () => buildRelacionFootCells(resolved, 'TOTAL CORTESIA', totalsCortesia),
    [resolved, totalsCortesia]
  );

  const tituloMostrar = String(enc.reporteTitulo || reporteTitulo).toUpperCase();

  return (
    <div className={`dc-report rv-report pop-report ${className}`.trim()}>
      <header className="pop-report-headerenc">
        {encabezadoTieneDatos(enc) &&
        enc.bloqueTexto &&
        !enc.nombreComercial &&
        !enc.reporteTitulo ? (
          <pre className="pop-report-headerenc__bloque">{enc.bloqueTexto}</pre>
        ) : (
          <>
            {(meaningfulHeaderLine(enc.nombreComercial) ||
              meaningfulHeaderLine(enc.domicilioFiscal) ||
              meaningfulHeaderLine(enc.rfc)) && (
              <div className="pop-report-headerenc__empresa">
                {meaningfulHeaderLine(enc.nombreComercial) && (
                  <h1 className="pop-report-headerenc__nombre">
                    {String(enc.nombreComercial).trim().toUpperCase()}
                    {sucursalNombre && !String(enc.nombreComercial).toUpperCase().includes(sucursalNombre.toUpperCase()) && (
                      <span className="pop-report-headerenc__sucursal-append"> - {sucursalNombre.toUpperCase()}</span>
                    )}
                  </h1>
                )}
                {meaningfulHeaderLine(enc.domicilioFiscal) && (
                  <p className="pop-report-headerenc__detalle rv-report-domicilio">
                    {String(enc.domicilioFiscal).trim()}
                  </p>
                )}
                {meaningfulHeaderLine(enc.rfc) && (
                  <p className="pop-report-headerenc__detalle pop-report-headerenc__rfc">
                    RFC: {String(enc.rfc).trim().toUpperCase()}
                  </p>
                )}
              </div>
            )}
            <div className="pop-report-headerenc__titulos">
              <h2 className="pop-report-headerenc__reporte dc-report-title-highlight">{tituloMostrar}</h2>
            </div>
            <div className="pop-report-headerenc__filtros">
              <p className="pop-report-headerenc__filtros-line">{enc.periodoTexto}</p>
              {enc.filtrosTexto && (
                <p className="pop-report-headerenc__filtros-line pop-report-headerenc__filtros-line--extra">
                  {enc.filtrosTexto}
                </p>
              )}
              <ReporteFiltrosEncabezadoLista
                className="reporte-filtros-meta--report"
                label="Sucursales"
                texto={enc.sucursalesTexto || sucursalNombre}
                items={enc.sucursalesItems}
              />
              <ReporteFiltrosEncabezadoLista
                className="reporte-filtros-meta--report"
                label="Cajas"
                texto={
                  enc.cajasTexto ||
                  (cajaInicio != null || cajaFin != null
                    ? `${cajaInicio ?? '—'} a ${cajaFin ?? '—'}`
                    : '')
                }
                items={enc.cajasItems}
              />
              <p className="pop-report-headerenc__filtros-line">
                {enc.turnosTexto ? (
                  <>Turnos: {String(enc.turnosTexto)}</>
                ) : (
                  <>
                    De: <strong>{turnoInicio ?? '—'}</strong> a <strong>{turnoFin ?? '—'}</strong>
                  </>
                )}
              </p>
            </div>
          </>
        )}
      </header>

      <div className="dc-report-table-wrap">
        <table className="dc-report-table rv-report-table">
          <thead>
            <tr>
              {resolved.map((c) => (
                <th key={c.id} className={c.align === 'right' ? 'dc-num' : 'dc-text'}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {segments.length === 0 ? (
              <tr>
                <td className="dc-report-empty-row" colSpan={resolved.length}>
                  Sin movimientos en el rango seleccionado.
                </td>
              </tr>
            ) : (
              segments.map((seg, idx) => {
                if (seg.kind === 'turnGroup') {
                  return (
                    <tr key={`t-${idx}`} className="rv-report-turn-group-row">
                      <td colSpan={resolved.length}>{String(seg.label)}</td>
                    </tr>
                  );
                }
                if (seg.kind === 'group') {
                  return (
                    <tr key={`g-${idx}`} className="rv-report-group-row">
                      <td colSpan={resolved.length}>{String(seg.label).toUpperCase()}</td>
                    </tr>
                  );
                }
                const norm = rowToRelacionNorm(seg.raw, resolved);
                return (
                  <tr key={`r-${idx}`}>
                    {resolved.map((c) => (
                      <td key={c.id} className={c.align === 'right' ? 'dc-num' : 'dc-text'}>
                        {formatRelacionCell(c, norm[c.id])}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
          <tfoot>
            <tr className="dc-report-totals">
              {resolved.map((c, i) => (
                <td
                  key={c.id}
                  className={
                    c.total ? 'dc-num dc-report-totals-num' : 'dc-text dc-report-totals-spacer'
                  }
                >
                  {footVenta[i]}
                </td>
              ))}
            </tr>
            <tr className="dc-report-totals rv-report-foot-cortesia">
              {resolved.map((c, i) => (
                <td
                  key={`c-${c.id}`}
                  className={
                    c.total ? 'dc-num dc-report-totals-num' : 'dc-text dc-report-totals-spacer'
                  }
                >
                  {footCortesia[i]}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
